import { useCallback } from "react";

import { listChatAttachments } from "~/entities/chat";
import type { ChatAttachment } from "~/entities/chat";

import { useChatMediaPicker } from "../useChatMediaPicker";
import type { PerKindCounts, PerKindLimits, PickerSourceItem, UseAttachmentPickerResult } from "../types";
import { fileNameFromKey } from "~/shared/lib/uploads/file-name";

// Re-export для обратной совместимости с существующими тестами / импортами.
export type { PickerImage } from "../types";

export interface UsePickUploadedAttachmentParams {
    chatId: string | null;
    /** Per-kind лимиты режима (`mode.maxInputImages|Videos|Audio`). */
    limits: PerKindLimits;
    /** Уже в pendingByChat для каждого kind. */
    currentCounts: PerKindCounts;
    enabled: boolean;
}

export type UsePickUploadedAttachmentResult = UseAttachmentPickerResult;

// Дедуп по `s3Key` делает БД: после CAS-дедупа референсов одно и то же
// содержимое переиспользуется в разных сообщениях, и пока склейка шла на фронте
// — ПОСЛЕ серверной обрезки — лимит по строкам молча уносил уникальные картинки
// (356 строк → 50 медиа, до пикера доходили 37).
const toItem = (a: ChatAttachment): PickerSourceItem => ({
    id: a.id,
    kind: a.kind,
    s3Key: a.s3Key,
    s3Url: a.s3Url,
    fileName: fileNameFromKey(a.s3Key),
    sizeBytes: a.sizeBytes,
    mimeType: a.mimeType,
    createdAt: a.createdAt,
});

export function usePickUploadedAttachment({
    chatId,
    limits,
    currentCounts,
    enabled,
}: UsePickUploadedAttachmentParams): UsePickUploadedAttachmentResult {
    // Без kind-фильтра — забираем все uploaded attachment'ы чата
    // (image/video/audio), фильтрация по kind делается клиентски через tabs
    // и `acceptedKinds` в useAttachmentPicker.
    const loadPage = useCallback(async (chatId: string, cursor: string | undefined, limit: number) => {
        const res = await listChatAttachments(chatId, { status: "uploaded", limit, before: cursor });
        return { items: res.attachments, total: res.total };
    }, []);

    return useChatMediaPicker<ChatAttachment>({
        chatId,
        limits,
        currentCounts,
        enabled,
        loadPage,
        toItem,
    });
}
